/**
 * <GlossaryTerm word="NDVI">NDVI</GlossaryTerm>
 *
 * Wraps any text with a dotted underline + tiny info dot. On hover (or
 * tap, mobile-friendly), a mini popover shows the glossary entry plus
 * a "→ ganzes Glossar"-link. Falls back to a native <title> if the
 * word isn't found, so usage is safe.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Info, ArrowRight } from "@phosphor-icons/react";
import { findTerm, CATEGORY_COLOR } from "@/lib/glossary";

export default function GlossaryTerm({
  word,
  children,
  className = "",
  underline = true,
}) {
  const entry = findTerm(word || children);
  const [open, setOpen] = useState(false);

  if (!entry) {
    // Quietly degrade — just render the children. This keeps page
    // authors from worrying about every term existing in glossary.js.
    return <span className={className}>{children}</span>;
  }

  const color = CATEGORY_COLOR[entry.category] || "#00FA9A";

  return (
    <span
      className={`relative inline-flex items-baseline gap-0.5 ${className}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onClick={() => setOpen((v) => !v)}
      onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }}
      data-testid={`glossary-term-inline-${entry.id}`}
    >
      <span
        className="cursor-help"
        style={
          underline
            ? {
                textDecoration: "underline dotted",
                textDecorationColor: color,
                textDecorationThickness: "1.5px",
                textUnderlineOffset: "3px",
              }
            : undefined
        }
        tabIndex={0}
      >
        {children}
      </span>
      <Info size={10} className="opacity-50 shrink-0" style={{ color, marginLeft: 1 }} />

      <AnimatePresence>
        {open && (
          <motion.span
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.2 }}
            className="absolute left-0 top-full mt-1 w-72 bg-[#F5F5F5] text-[#0A0A0A] rounded-sm shadow-[0_0_0_1px_rgba(255,255,255,0.06)] z-50 font-normal pointer-events-auto"
            role="tooltip"
          >
            <span className="block px-3 pt-2 pb-1 border-b border-white/10 flex items-center justify-between gap-2 text-[10px] uppercase tracking-widest font-mono">
              <span className="font-display text-sm font-bold tracking-tight text-white normal-case tracking-tighter">
                {entry.term}
              </span>
              <span style={{ color }}>{entry.category}</span>
            </span>
            <span className="block px-3 py-2 text-[12px] leading-relaxed text-white/90">
              {entry.answer}
            </span>
            <Link
              to={`/glossar#${entry.id}`}
              className="block border-t border-white/10 px-3 py-1.5 text-[10px] uppercase tracking-widest font-mono hover:bg-white/5"
              style={{ color }}
              onClick={(e) => e.stopPropagation()}
            >
              <ArrowRight size={10} className="inline mr-1" /> Im Glossar lesen
            </Link>
          </motion.span>
        )}
      </AnimatePresence>
    </span>
  );
}
